import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const LEVEL_LABEL = { REQUIRED: "Indispensable", RECOMMENDED: "Recommandé", OPTIONAL: "Optionnel", NOT_APPLICABLE: "Non applicable" };
const LEVEL_CLS = { REQUIRED: "bg-red-50 text-red-700 border-red-200", RECOMMENDED: "bg-blue-50 text-blue-700 border-blue-200", OPTIONAL: "bg-amber-50 text-amber-700 border-amber-200", NOT_APPLICABLE: "bg-slate-100 text-slate-500 border-slate-200" };

export const AdminSectors = () => {
  const [data, setData] = useState(null);
  const [sector, setSector] = useState("");
  const [rules, setRules] = useState({});
  const [filter, setFilter] = useState("");
  const [newSector, setNewSector] = useState({ code: "", name: "" });

  const load = () => api.get("/admin/sector-rules").then((r) => { setData(r.data); if (!sector && r.data.sectors[0]) setSector(r.data.sectors[0].code); });
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (data && sector) setRules(data.rules[sector] || {}); }, [data, sector]);

  const save = async () => {
    try { await api.put(`/admin/sector-rules/${sector}`, { rules }); toast.success("Règles enregistrées — appliquées au prochain audit"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const addSector = async () => {
    if (!newSector.code || !newSector.name) return;
    await api.post("/admin/sectors", newSector); toast.success("Secteur ajouté"); setNewSector({ code: "", name: "" }); load();
  };

  if (!data) return null;
  const checks = data.checks.filter((c) => !filter || c.label.toLowerCase().includes(filter.toLowerCase()) || c.key.includes(filter.toLowerCase()));
  const counts = data.levels.reduce((a, l) => ({ ...a, [l]: data.checks.filter((c) => (rules[c.key] || "RECOMMENDED") === l).length }), {});

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-5" data-testid="admin-sectors">
      <div>
        <h2 className="font-heading font-semibold">Matrice de pertinence par secteur</h2>
        <p className="text-xs text-slate-500 mt-1">Pour chaque contrôle : Indispensable (poids ×1,5), Recommandé (×1), Optionnel (×0,5), Non applicable (exclu du score, tâches archivées). Sans réglage, un contrôle est Recommandé.</p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {data.sectors.map((s) => (
          <button key={s.code} onClick={() => setSector(s.code)} data-testid={`sector-tab-${s.code}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${sector === s.code ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{s.name}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div><label className="text-xs text-slate-400">Nouveau secteur (code)</label><Input value={newSector.code} onChange={(e) => setNewSector({ ...newSector, code: e.target.value.toLowerCase() })} placeholder="ex : fleuriste" className="mt-1 w-40" data-testid="new-sector-code" /></div>
        <div><label className="text-xs text-slate-400">Nom affiché</label><Input value={newSector.name} onChange={(e) => setNewSector({ ...newSector, name: e.target.value })} placeholder="Fleuriste" className="mt-1 w-48" data-testid="new-sector-name" /></div>
        <Button variant="outline" onClick={addSector} className="rounded-xl" data-testid="add-sector-button">Ajouter le secteur</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer les contrôles…" className="w-64" data-testid="sector-rules-filter" />
        <div className="flex gap-2 text-xs">{data.levels.map((l) => <span key={l} className={`px-2 py-0.5 rounded-full border ${LEVEL_CLS[l]}`}>{LEVEL_LABEL[l]} : {counts[l]}</span>)}</div>
        <Button onClick={save} className="ml-auto bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="save-sector-rules-button">Enregistrer pour « {sector} »</Button>
      </div>
      <div className="divide-y divide-slate-100 max-h-[60vh] overflow-auto rounded-xl border border-slate-100">
        {checks.map((c) => {
          const lvl = rules[c.key] || "RECOMMENDED";
          return (
            <div key={c.key} className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 text-sm" data-testid={`rule-row-${c.key}`}>
              <div className="flex-1 min-w-0"><p className="text-slate-800 truncate">{c.label}</p><p className="text-[11px] text-slate-400 font-mono">{c.group} · {c.key}</p></div>
              <div className="flex gap-1 flex-wrap">
                {data.levels.map((l) => (
                  <button key={l} onClick={() => setRules({ ...rules, [c.key]: l })} data-testid={`rule-${c.key}-${l}`}
                    className={`text-[11px] px-2 py-1 rounded-lg border ${lvl === l ? LEVEL_CLS[l] + " font-semibold" : "border-transparent text-slate-400 hover:bg-slate-50"}`}>{LEVEL_LABEL[l]}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
